import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import logo from '../../assets/images/logo_blue.png';

import { isValidISODate, formatISOToPHT } from '../../utilities/date';

// Styles for the PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Courier',
    fontSize: 12,
    padding: 30,
    lineHeight: 1.5,
    flexDirection: 'column',
  },
  header: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 'auto',
    height: 30,
  },
  title: {
    position: 'absolute',
    left: 0,
    right: 0,
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  section: {
    marginBottom: 15,
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 15,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColField: {
    width: '40%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 1,
  },
  tableCol: {
    width: '60%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 1,
  },
  tableCell: {
    fontSize: 10,
    flexWrap: 'wrap',
    wordBreak: 'break-all',
  },
});

const employeeFieldsRemove = [
  'deptid',
  'jobid',
  'parentid',
  'typeid',
  'catid',
  'password',
  'role',
  'active',
  'salutation',
  'gender',
  'race',
  'webpage',
  'homephone',
  'officephone',
  'datejoined',
  'lastlogindate',
  'loginip',
  'lastproject',
  'islocked',
  'is2faenabled',
  'base32',
  'otpauthurl',
  'deactivated',
  'regularhours',
  'numlogins',
  'ipsignup',
  'ipupdated',
  '__v',
  'address1',
  'address2',
  'city',
  'country',
  'marital',
  'minit',
  'ssn',
  'state',
  'zipcode',
];

const commonFieldsRemove = ['empid', '_id', '__v'];

const historyFallback = {
  formallycharged: false,
  chargedetails: '',
  guiltyoffense: false,
  offensedetails: '',
  convicted: false,
  convictdetails: '',
  terminatedemployment: false,
  employmentdetails: '',
  indigenousmember: false,
  indigenousdetails: '',
  diffabled: false,
  diffableddetails: '',
  soloparent: false,
  soloparentdetails: '',
};

const cleanObject = (obj, excludeKeys = []) => {
  if (!obj || typeof obj !== 'object') return []; // Catch undefined, null, or non-objects
  return Object.entries(obj).filter(([key]) => !excludeKeys.includes(key));
};

const cleanEmployee = (employeeObj) =>
  cleanObject(employeeObj, employeeFieldsRemove);

const cleanHRIS = (hrisObj) => cleanObject(hrisObj, commonFieldsRemove);

const Header = () => (
  <View style={styles.header}>
    <Image src={logo} style={styles.logo} />
    <Text style={styles.title}>Employee Data</Text>
  </View>
);

const DataPage = ({ header, title, data }) => {
  if (!data || data.length === 0) return null;
  return (
    <Page size="A4" style={styles.page}>
      <Header />
      <View style={styles.section}>
        <Text style={{ marginBottom: 5, fontWeight: 'bold' }}>{header}:</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
          {data.map((item, index) => {
            const cleaned = cleanHRIS(item);
            if (cleaned.length === 0) return null;
            return (
              <View
                key={index}
                style={{
                  flexBasis: '48%',
                  padding: 3,
                }}
              >
                <Text style={{ fontWeight: 'bold', marginBottom: 3 }}>
                  {title} {index + 1}
                </Text>
                <View style={styles.table} wrap>
                  {/* Table Header */}
                  <View style={styles.tableRow}>
                    <View style={styles.tableColField}>
                      <Text style={styles.tableCell}>Field</Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>Value</Text>
                    </View>
                  </View>
                  {/* Table Rows */}
                  {cleaned.map(([key, value]) => (
                    <View style={styles.tableRow} key={key}>
                      <View style={styles.tableColField}>
                        <Text style={styles.tableCell}>
                          {key.toUpperCase()}
                        </Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>
                          {typeof value === 'boolean'
                            ? value
                              ? 'Yes'
                              : 'No'
                            : isValidISODate(value)
                              ? formatISOToPHT(value)
                              : value}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            );
          })}
        </View>
      </View>
    </Page>
  );
};

export const EmployeeDataPDF = ({
  employee,
  empaddress,
  emprelations,
  empeducation,
  empcivservice,
  empworkexp,
  empcivicwork,
  emptraining,
  emphistory,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Header />
        <Text style={{ textAlign: 'center', fontSize: 10, color: 'grey' }}>
          Generated on {new Date().toLocaleDateString()}
        </Text>
        {/* Employee Info */}
        <View style={styles.section}>
          <Text>
            Name:{' '}
            <Text style={{ fontWeight: 'bold' }}>
              {employee.lastname}, {employee.firstname} {employee.middlename}
            </Text>
          </Text>
          <Text>Email: {employee.email}</Text>
        </View>
        {/* Employee Details Table */}
        <View style={styles.section}>
          <Text style={{ marginBottom: 5, fontWeight: 'bold' }}>
            Employee Details:
          </Text>
          <View style={styles.table} wrap>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColField}>
                <Text style={styles.tableCell}>Field</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Value</Text>
              </View>
            </View>
            {/* Table Rows */}
            {cleanEmployee(employee).map(([key, value]) => (
              <View style={styles.tableRow} key={key}>
                <View style={styles.tableColField}>
                  <Text style={styles.tableCell}>{key.toUpperCase()}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {isValidISODate(value) ? formatISOToPHT(value) : value}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>
      <DataPage header="Addresses" title="Address" data={empaddress} />
      <DataPage header="Relations" title="Relation" data={emprelations} />
      <DataPage header="Educations" title="Education" data={empeducation} />
      <DataPage
        header="Civil Service Eligibilities"
        title="Eligibility"
        data={empcivservice}
      />
      <DataPage
        header="Work Experiences"
        title="Experience"
        data={empworkexp}
      />
      <DataPage header="Voluntary Works" title="Vol Work" data={empcivicwork} />
      <DataPage header="Trainings" title="Training" data={emptraining} />

      {/* History Page */}
      <Page size="A4" style={styles.page}>
        <Header />

        {/* Employee History Table */}
        <View style={styles.section}>
          <Text style={{ marginBottom: 5, fontWeight: 'bold' }}>
            Employee History:
          </Text>
          <View style={styles.table} wrap>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColField}>
                <Text style={styles.tableCell}>Field</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>Value</Text>
              </View>
            </View>
            {/* Table Rows */}
            {(cleanHRIS(emphistory).length > 0
              ? cleanHRIS(emphistory)
              : Object.entries(historyFallback)
            ).map(([key, value]) => (
              <View style={styles.tableRow} key={key}>
                <View style={styles.tableColField}>
                  <Text style={styles.tableCell}>{key.toUpperCase()}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    {typeof value === 'boolean'
                      ? value
                        ? 'Yes'
                        : 'No'
                      : isValidISODate(value)
                        ? formatISOToPHT(value)
                        : value}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};
